import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { Checkbox } from '@rbilabs/universal-components';
import * as Notifications from 'expo-notifications';
import { NotificationPermissionsStatus } from 'expo-notifications';
import { AppState, AppStateStatus, NativeEventSubscription, StyleSheet } from 'react-native';

import ConfirmDialog from 'components/confirm-dialog';
import { InstallAppButton } from 'components/install-app-button';
import LoadingAnimation from 'components/loading-animation';
import { useLoyaltyQuestQuery } from 'components/quests/loyalty-quest/use-loyalty-quest-query';
import { UserJourneyFormLabel } from 'components/quests/user-jouney-form-label';
import { toaster } from 'components/toaster/toaster';
import { TextInput } from 'components/ucl/text-input';
import { Box, Typography } from 'components/ui';
import { OptInLabel } from 'components/user-info-form/styled';
import { IUserInfoSubmitData } from 'components/user-info-form/types';
import {
  CommunicationPreferenceType,
  IsoCountryCode,
  LoyaltyCmsType,
  useUpdateUserCommunicationPreferencesMutation,
} from 'generated/graphql-gateway';
import useIsComponentMounted from 'hooks/use-is-component-mounted';
import { routes } from 'navigation/routing';
import {
  UserJourneyFields,
  UserJourneySections,
  getUserJourneyStepsForSection,
} from 'pages/loyalty/loyalty-user-journey/utils';
import { logCommunicationPreferenceUpdated } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import { t, tRich } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { primitive, spacing } from 'styles/constants';
import { isNative, isWeb } from 'utils/environment';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { getPhoneNumberErrorMessage, isPhoneNumberValid, sanitizePhoneNumber } from 'utils/form';
import logger from 'utils/logger';
import { enablePush, openPushSetting } from 'utils/permissions';

import { StyledOptInLink, ToggleButton } from './styled';

export const SMS_SUBSCRIPTION_EXP_TIME_MIN = 3;
export const EMAIL_SUBSCRIPTION_EXP_TIME_MIN = 3;
export const PUSH_SUBSCRIPTION_EXP_TIME_MIN = 3;

const COMMUNICATION_PREFERENCE_CHANNEL: Record<
  CommunicationPreferenceType,
  'Email' | 'Push' | 'SMS'
> = {
  [CommunicationPreferenceType.MARKETINGEMAIL]: 'Email',
  [CommunicationPreferenceType.MARKETINGPUSH]: 'Push',
  [CommunicationPreferenceType.MARKETINGSMS]: 'SMS',
};

interface ICommCardEmailPush {
  marketingEmail: boolean;
  marketingPush: boolean;
  marketingSms: boolean;
  setMarketingEmailPrefs: React.Dispatch<React.SetStateAction<boolean>>;
  setPushPrefs: React.Dispatch<React.SetStateAction<boolean>>;
  setMarketingSmsPrefs: React.Dispatch<React.SetStateAction<boolean>>;
  userPhoneNumber?: string;
  setIsNewPhoneNumberValid: Dispatch<SetStateAction<boolean>>;
  setOnUpdatePhoneNumber: Dispatch<SetStateAction<(() => Promise<void>) | undefined>>;
}

const ToggleButtonWithLoading = ({
  label,
  description,
  isDescriptionDisabled,
  isLoading,
  children,
  hideBottomBorder,
}: {
  children: React.ReactNode;
  label: React.ReactNode;
  description?: string;
  isDescriptionDisabled?: boolean;
  isLoading?: boolean;
  hideBottomBorder?: boolean;
}) => {
  return (
    <Box
      style={[styles.toggleContainer, !hideBottomBorder && styles.toggleContainerWithBottomBorder]}
    >
      <Box style={styles.toggleLabelContainer}>
        {typeof label === 'string' ? (
          <Typography variant="copyTwo" fontSize="md">
            {label}
          </Typography>
        ) : (
          label
        )}
        {description && (
          <Typography color={isDescriptionDisabled ? primitive.$blackOpacity50 : undefined}>
            {description}
          </Typography>
        )}
      </Box>
      {isLoading ? (
        <Box style={styles.loadingToggleContainer}>
          <LoadingAnimation size="sm" />
        </Box>
      ) : (
        children
      )}
    </Box>
  );
};

const CommCardEmailPush: React.FC<ICommCardEmailPush> = ({
  marketingEmail,
  marketingPush,
  marketingSms,
  setMarketingEmailPrefs,
  setPushPrefs,
  setMarketingSmsPrefs,
  userPhoneNumber,
  setOnUpdatePhoneNumber,
  setIsNewPhoneNumberValid,
}: ICommCardEmailPush) => {
  const { user, updateUserInfo } = useAuthContext();
  const smsSubscriptionEnabled = useFlag(LaunchDarklyFlag.ENABLE_SMS_MARKETING_SUBSCRIPTIONS);
  const [notificationPermissionsStatus, setNotificationPermissionsStatus] = useState<
    NotificationPermissionsStatus
  >();
  const [showPushDialog, setShowPushDialog] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [acceptMarketingSMSCommPrefs, setAcceptMarketingSMSCommPrefs] = useState(false);
  const [privacyPolicyChecked, setPrivacyPolicyChecked] = useState(false);
  const isMounted = useIsComponentMounted();
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [touchedPhoneNumber, setTouchedPhoneNumber] = useState(false);
  const [isSubmittingPhoneNumber, setIsSubmittingPhoneNumber] = useState(false);
  const { region } = useLocale();

  const [
    updateUserCommunicationPreferencesMutation,
  ] = useUpdateUserCommunicationPreferencesMutation();

  const { loyaltyUserId } = useLoyaltyUserId();
  const { quest: challenge } = useLoyaltyQuestQuery({
    loyaltyId: loyaltyUserId ?? '',
    cmsType: LoyaltyCmsType.USER_JOURNEY,
  });

  const challengeSteps =
    (challenge && getUserJourneyStepsForSection(challenge, UserJourneySections.Communications)) ||
    {};

  const getCurrentPreferenceStatus = (communicationPreferenceType: CommunicationPreferenceType) => {
    switch (communicationPreferenceType) {
      case CommunicationPreferenceType.MARKETINGEMAIL:
        return marketingEmail;
      case CommunicationPreferenceType.MARKETINGPUSH:
        return marketingPush;
      case CommunicationPreferenceType.MARKETINGSMS:
        return marketingSms;
      default:
        return false;
    }
  };

  /**
   * Updates user preferences in Braze/DynamoDB
   */
  const changeSubscriptionStatus = async (
    communicationPreferenceType: CommunicationPreferenceType,
    newStatus: boolean,
    options?: { trackPreferenceUpdate?: boolean }
  ) => {
    const trackPreferenceUpdate = options?.trackPreferenceUpdate ?? true;
    const previousStatus = getCurrentPreferenceStatus(communicationPreferenceType);
    const didPreferenceChange = previousStatus !== newStatus;

    try {
      const { data } = await updateUserCommunicationPreferencesMutation({
        variables: {
          input: {
            communicationPreferenceType,
            newStatus,
          },
        },
      });

      if (trackPreferenceUpdate && didPreferenceChange) {
        logCommunicationPreferenceUpdated({
          channel: COMMUNICATION_PREFERENCE_CHANNEL[communicationPreferenceType],
          action: newStatus ? 'Opt-In' : 'Opt-Out',
        });
      }

      switch (communicationPreferenceType) {
        case CommunicationPreferenceType.MARKETINGEMAIL:
          setMarketingEmailPrefs(!!data?.updateUserCommunicationPreferences.marketingEmail);
          syncStorage.setObjectWithExpiration(
            SyncStorageKey.MARKETING_EMAIL_SUBSCRIPTION_REQUESTED,
            newStatus,
            EMAIL_SUBSCRIPTION_EXP_TIME_MIN
          );
          break;
        case CommunicationPreferenceType.MARKETINGPUSH:
          setPushPrefs(!!data?.updateUserCommunicationPreferences.marketingPush);
          syncStorage.setObjectWithExpiration(
            SyncStorageKey.MARKETING_PUSH_SUBSCRIPTION_REQUESTED,
            newStatus,
            PUSH_SUBSCRIPTION_EXP_TIME_MIN
          );
          break;
        case CommunicationPreferenceType.MARKETINGSMS:
          setMarketingSmsPrefs(!!data?.updateUserCommunicationPreferences.marketingSms);
          // We store this value in local storage to keep displaying the toggle as ON in case the user
          // exits and re-enters the Preference Center without having validated the subscription by
          // sending an SMS from their device..
          if (newStatus) {
            toaster.positive(t('smsSubscriptionConfirmation'));
          }

          syncStorage.setObjectWithExpiration(
            SyncStorageKey.MARKETING_SMS_SUBSCRIPTION_REQUESTED,
            newStatus,
            SMS_SUBSCRIPTION_EXP_TIME_MIN
          );
          break;
        default:
          break;
      }
    } catch (error) {
      logger.error({
        error,
        message: `Failed to update ${communicationPreferenceType} subscription status`,
      });
      toaster.negative(t('smsSubscriptionUpdateError'));
    }
  };

  /**
   * Compares the device permissions and the Braze permissions.
   * If Braze's marketingEmail is true, but the user doesn't have Push Notifications enabled,
   * a popup will be prompted to change the device preferences, asking user for Push
   * Notification permissions.
   */
  const checkPushPermissions = async (pushPermissionsStatus: boolean) => {
    if (!isNative) {
      return;
    }

    const pushPermissionsGranted = !!notificationPermissionsStatus?.granted;

    // If Braze is true and device is false: Ask user for permissions
    if (pushPermissionsStatus && !pushPermissionsGranted) {
      if (notificationPermissionsStatus?.canAskAgain) {
        const settings = await enablePush();
        if (settings) {
          setNotificationPermissionsStatus(settings);
          if (settings.granted) {
            changeSubscriptionStatus(CommunicationPreferenceType.MARKETINGPUSH, true);
          }
        }
      } else {
        setShowPushDialog(true);
      }
    }
  };

  /**
   * Gets the status of the Push Notification permissions from the user's device
   */
  const loadDeviceSettings = async (updateBraze: boolean) => {
    const settings = await Notifications.getPermissionsAsync();
    setNotificationPermissionsStatus(settings);
    if (updateBraze && settings.granted) {
      handlePushNotificationToggle(true);
    }
  };

  const handleAppStateChange = (nextAppState: AppStateStatus) => {
    if (nextAppState === 'active') {
      // The app has returned from the background (e.g., from device settings)
      // update the device settings
      loadDeviceSettings(true);
    }
  };

  const handlePushNotificationToggle = async (isChecked: boolean) => {
    await changeSubscriptionStatus(CommunicationPreferenceType.MARKETINGPUSH, isChecked);
  };

  const closePushNotificationsDialog = () => {
    setShowPushDialog(false);
  };

  const handleSmsSubscription = async (isChecked: boolean) => {
    if (!marketingSms && isChecked && (!acceptMarketingSMSCommPrefs || !privacyPolicyChecked)) {
      toaster.negative(t('agreeToTheTerms'));
      return;
    }
    setMarketingSmsPrefs(isChecked);

    await changeSubscriptionStatus(CommunicationPreferenceType.MARKETINGSMS, isChecked);
  };

  const handleUpdatePhoneNumber = async (phoneNumber: string) => {
    if (!user?.details) {
      return;
    }
    setIsSubmittingPhoneNumber(true);
    // Save the new phone number
    const isoCountryCode = (user?.details?.isoCountryCode as IsoCountryCode) || IsoCountryCode.USA;
    const formattedPhoneNumber = await sanitizePhoneNumber(phoneNumber, isoCountryCode);

    const userForm: IUserInfoSubmitData = {
      ...user?.details,
      adsPersonalizationOptOut: user?.details.adsPersonalizationOptOut ?? false,
      agreesToTermsOfService: true,
      isoCountryCode,
      phoneNumber: formattedPhoneNumber,
    };
    await updateUserInfo(userForm);

    // Set the SMS toggle to false
    await changeSubscriptionStatus(CommunicationPreferenceType.MARKETINGSMS, false, {
      trackPreferenceUpdate: false,
    });

    setAcceptMarketingSMSCommPrefs(false);
    setPrivacyPolicyChecked(false);

    setIsNewPhoneNumberValid(false);
    setIsSubmittingPhoneNumber(false);
  };

  const getFormErrors = async ({ phoneNumber }: { phoneNumber: string }) => {
    const phoneNumberValidation = phoneNumber
      ? await isPhoneNumberValid({
          phoneNumber,
          country: region,
        })
      : { valid: true };

    if (!phoneNumberValidation.valid) {
      setPhoneNumberError(getPhoneNumberErrorMessage(phoneNumberValidation.error));
    } else {
      setPhoneNumberError('');
    }

    /**
     * A user is allowed to update their phone number when:
     * - The new number is valid and different from the one stored in the user's profile.
     * - The phone number is empty (removal is allowed).
     */
    const canUpdatePhoneNumber =
      (!!phoneNumber && phoneNumber !== userPhoneNumber && phoneNumberValidation.valid) ||
      !phoneNumber;

    setIsNewPhoneNumberValid(canUpdatePhoneNumber);
  };

  useEffect(() => {
    setOnUpdatePhoneNumber(() => () => handleUpdatePhoneNumber(newPhoneNumber));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPhoneNumber, setOnUpdatePhoneNumber]);

  useEffect(() => {
    if (marketingPush && !!notificationPermissionsStatus) {
      checkPushPermissions(marketingPush);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketingPush, notificationPermissionsStatus]);

  useEffect(() => {
    let subscription: NativeEventSubscription;
    if (isNative && isMounted) {
      loadDeviceSettings(false);

      subscription = AppState.addEventListener('change', handleAppStateChange);
    }
    return () => subscription?.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNative, isMounted]);

  useEffect(() => {
    setNewPhoneNumber(userPhoneNumber ?? '');
  }, [userPhoneNumber]);

  return (
    <>
      <Box style={styles.preferencesContainer}>
        <Typography variant="headerThree" fontSize="lg" style={styles.commPreferenceLabel}>
          {t('emailPushPreferencesTitle')}
        </Typography>
        <Typography fontSize="md" style={styles.commPreferenceDescription}>
          {t('emailPushPreferencesDescription')}
        </Typography>

        {/* Email Subscription Button */}
        <ToggleButtonWithLoading
          label={
            challengeSteps[UserJourneyFields.Email] ? (
              <UserJourneyFormLabel
                label={t('email')}
                step={challengeSteps[UserJourneyFields.Email]}
                fieldName={UserJourneyFields.Email}
                fieldType="boolean"
                inline
              />
            ) : (
              t('email')
            )
          }
        >
          <ToggleButton
            aria-label="marketing email switch button"
            name="CommEmail"
            isChecked={marketingEmail}
            onValueChange={isChecked => {
              setMarketingEmailPrefs(isChecked);
              changeSubscriptionStatus(CommunicationPreferenceType.MARKETINGEMAIL, isChecked);
            }}
            testID="CommEmail-marketing-switch"
          />
        </ToggleButtonWithLoading>

        <ToggleButtonWithLoading
          label={
            challengeSteps[UserJourneyFields.PushNotification] ? (
              <UserJourneyFormLabel
                label={t('push')}
                step={challengeSteps[UserJourneyFields.PushNotification]}
                fieldName={UserJourneyFields.PushNotification}
                fieldType="boolean"
                inline
              />
            ) : (
              t('push')
            )
          }
        >
          {isNative && (
            <ToggleButton
              aria-label="push notifications switch button"
              name="CommMobile"
              isChecked={marketingPush}
              onValueChange={isChecked => {
                setPushPrefs(isChecked);
                if (isChecked && !notificationPermissionsStatus?.granted && isNative) {
                  checkPushPermissions(isChecked);
                  return;
                }
                handlePushNotificationToggle(isChecked);
              }}
              testID="CommMobile-marketing-push"
            />
          )}
          {isWeb && <InstallAppButton />}
        </ToggleButtonWithLoading>

        {/* SMS Subscription Button */}
        {smsSubscriptionEnabled && (
          <Box style={styles.inputContainer}>
            <TextInput
              noMargin
              labelProps={{ marginTop: '$4' }}
              accessibilityLabel={t('phoneNumber')}
              onChangeText={(phoneNumber: string) => {
                getFormErrors({ phoneNumber });
                setNewPhoneNumber(phoneNumber.trim());
              }}
              onBlur={() => {
                setTouchedPhoneNumber(true);
              }}
              label={t('phoneNumber')}
              value={newPhoneNumber}
              placeholder={t('phoneNumber')}
              testID="sms-phone-number"
              errorMessage={touchedPhoneNumber ? phoneNumberError : ''}
              isDisabled={isSubmittingPhoneNumber}
            />
            <ToggleButtonWithLoading
              hideBottomBorder={true}
              label={
                challengeSteps[UserJourneyFields.PhoneNumber] ? (
                  <UserJourneyFormLabel
                    label={t('sms')}
                    step={challengeSteps[UserJourneyFields.PhoneNumber]}
                    fieldName={UserJourneyFields.PhoneNumber}
                    fieldType="boolean"
                    inline
                  />
                ) : (
                  t('sms')
                )
              }
            >
              <ToggleButton
                aria-label="SMS marketing subscription toggle button"
                isChecked={marketingSms}
                name="CommSMS"
                onValueChange={handleSmsSubscription}
                testID="CommSMS-marketing-switch"
                disabled={
                  isSubmittingPhoneNumber || userPhoneNumber !== newPhoneNumber || !userPhoneNumber
                }
              />
            </ToggleButtonWithLoading>
            {!marketingSms && (
              <Box style={styles.permissionsContainer}>
                <Box style={styles.smsCheckboxRow}>
                  <Checkbox
                    value={'accept-marketing-sms-comm-prefs'}
                    aria-label={t('acceptMarketingSMSCommPrefs')}
                    isChecked={acceptMarketingSMSCommPrefs}
                    onChange={checked => {
                      setAcceptMarketingSMSCommPrefs(checked);
                    }}
                  />
                  <OptInLabel>{t('acceptMarketingSMSCommPrefs')}</OptInLabel>
                </Box>
                <Box style={styles.privacyCheckboxRow}>
                  <Checkbox
                    value={'accept-privacy-policy'}
                    aria-label={t('acceptTermsAndPrivacyCommPrefs')}
                    isChecked={privacyPolicyChecked}
                    onChange={checked => {
                      setPrivacyPolicyChecked(checked);
                    }}
                  />
                  <OptInLabel>
                    {tRich('acceptTermsAndPrivacyCommPrefs', {
                      bkRoyalPerksTextProgramLink: (
                        <StyledOptInLink to={routes.textTerms}>{t('textTerms')}</StyledOptInLink>
                      ),
                      privacyPolicyLink: (
                        <StyledOptInLink to={routes.privacyPolicy}>
                          {t('privacyPolicy')}
                        </StyledOptInLink>
                      ),
                      rewardsPolicyLink: (
                        <StyledOptInLink to={routes.termsOfService}>
                          {t('termsOfService')}
                        </StyledOptInLink>
                      ),
                    })}
                  </OptInLabel>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
      <ConfirmDialog
        showDialog={showPushDialog}
        heading={t('push')}
        body={t('enablePushNotificationsOnDevice')}
        cancelLabel={t('cancel')}
        confirmLabel={t('openDeviceSettings')}
        onDismiss={closePushNotificationsDialog}
        onCancel={closePushNotificationsDialog}
        onConfirm={() => {
          closePushNotificationsDialog();
          openPushSetting();
        }}
        modalAppearanceEventMessage="Confirm Open device Push Notification settings"
      />
    </>
  );
};

export default CommCardEmailPush;

const styles = StyleSheet.create({
  commPreferenceDescription: {
    margin: 0,
    marginBottom: spacing.$4,
  },
  commPreferenceLabel: {
    marginBottom: spacing.$2,
  },
  inputContainer: {
    marginBottom: spacing.$3,
    width: '100%',
  },
  loadingToggleContainer: {
    marginRight: spacing.$4,
  },
  permissionsContainer: {
    marginBottom: spacing.$3,
  },
  preferencesContainer: {
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  privacyCheckboxRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    marginBottom: spacing.$2,
  },
  smsCheckboxRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    marginBottom: spacing.$6,
  },
  toggleContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.$4,
    paddingRight: 2,
    paddingVertical: spacing.$4,
    width: '100%',
  },
  toggleContainerWithBottomBorder: {
    borderBottomColor: primitive.$blackOpacity10,
    borderBottomWidth: 1,
  },
  toggleLabelContainer: {
    flex: 1,
  },
});
